import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchNasHosts, fetchNasSummary } from '@/api/nas'
import { CloudSyncConnectionsPanel } from '@/components/CloudSyncConnectionsPanel'
import { NasHealthSummary } from '@/components/NasHealthSummary'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { CollectedAtEmphasis, joinCollapsedSummary } from '@/components/SnapshotCard'
import { formatDateTime } from '@/lib/formatAgent'
import {
  altaroStatusBadgeVariant,
  altaroStatusLabel,
} from '@/lib/altaroBackup'
import {
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

const FILTER_LABELS = {
  pass: 'Pass',
  warn: 'Warning',
  fail: 'Failed',
  no_data: 'No data',
  stale: 'Collection stale',
  updates: 'DSM update available',
}

function SummaryTile({ label, value, variant, active, onClick }) {
  const clickable = Boolean(onClick) && ((value ?? 0) > 0 || active)
  const Tag = clickable ? 'button' : 'div'

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-lg border border-border p-4 text-left transition-colors',
        clickable && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
        active && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        variant === 'success' && 'border-emerald-500/20 bg-emerald-500/5',
        variant === 'warning' && 'border-amber-500/30 bg-amber-500/5',
        variant === 'destructive' && 'border-destructive/40 bg-destructive/5',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value ?? 0}</p>
    </Tag>
  )
}

function IngestBadge({ status }) {
  if (status === 'active') return <Badge variant="success">Reporting</Badge>
  if (status === 'stale') return <Badge variant="warning">Collection stale</Badge>
  return <Badge variant="secondary">No data</Badge>
}

function PostureBadge({ status }) {
  const normalised = normaliseToolkitStatus(status)
  if (!normalised) return null
  const variant =
    normalised === 'PASS'
      ? 'success'
      : normalised === 'WARN'
        ? 'warning'
        : normalised === 'FAIL'
          ? 'destructive'
          : 'secondary'
  return <Badge variant={variant}>{toolkitStatusLabel(status)}</Badge>
}

function SourceCollectedAt({ label, collectedAt, ingestStatus, postureStatus }) {
  const hasData = ingestStatus !== 'never'

  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {hasData ? (
          <CollectedAtEmphasis collectedAt={collectedAt} prefix="" className="text-sm" />
        ) : (
          <p className="text-sm text-muted-foreground">No data</p>
        )}
        {hasData && <IngestBadge status={ingestStatus} />}
        {hasData && <PostureBadge status={postureStatus} />}
      </div>
    </div>
  )
}

function TaskSummaryTable({ tasks }) {
  const rows = tasks ?? []
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No Hyper Backup tasks found in the latest snapshot.
      </p>
    )
  }

  return (
    <div className="rounded-md border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
            <th className="px-3 py-2 font-medium">Backup task</th>
            <th className="px-3 py-2 font-medium">Last run</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((task, index) => (
            <tr
              key={`${task.task_name || 'unknown'}-${index}`}
              className="border-b border-border last:border-0 align-top"
            >
              <td className="px-3 py-2 font-medium">{task.task_name || 'Unknown task'}</td>
              <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                {formatDateTime(task.last_run_at)}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {task.status ? (
                  <Badge variant={altaroStatusBadgeVariant(task.status)}>
                    {altaroStatusLabel(task.status, task.status_label)}
                  </Badge>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function NasHostCard({ host }) {
  const [open, setOpen] = useState(false)
  const overall = host.overall_status ? toolkitStatusLabel(host.overall_status) : null
  const statusVariant = (() => {
    const normalised = normaliseToolkitStatus(host.overall_status)
    if (normalised === 'PASS') return 'ready'
    if (normalised === 'WARN') return 'pending'
    if (normalised === 'FAIL') return 'destructive'
    return 'inactive'
  })()

  const showHealth = host.health_ingest_status !== 'never' || Boolean(host.health)
  const showHyperBackup =
    host.hyperbackup_ingest_status !== 'never' || (host.tasks?.length ?? 0) > 0
  const showCloudSync =
    host.cloudsync_ingest_status !== 'never' || (host.cloudsync_connections?.length ?? 0) > 0

  const attentionReasons = Array.isArray(host.attention_reasons)
    ? host.attention_reasons
    : []
  const title = host.display_name || host.hostname
  const collapsedSummary = joinCollapsedSummary([
    host.customer_name || 'Synology NAS',
    overall,
    attentionReasons[0]
      ? `${attentionReasons[0].source}: ${attentionReasons[0].detail}`
      : null,
    host.task_count ? `${host.task_count} Hyper Backup` : null,
    host.cloudsync_count ? `${host.cloudsync_count} Cloud Sync` : null,
    host.ingest_status === 'never' ? (
      <CollectedAtEmphasis neverLabel="No data collected" />
    ) : host.collected_at ? (
      <CollectedAtEmphasis collectedAt={host.collected_at} />
    ) : null,
    host.ingest_status === 'stale' ? 'Collection stale' : null,
  ])

  return (
    <CollapsePanel
      id={`nas-host-${host.hostname}`}
      title={title}
      collapsedSummary={collapsedSummary || undefined}
      open={open}
      onOpenChange={setOpen}
      pill={overall || undefined}
      pillVariant={overall ? statusVariant : 'inactive'}
      className={cn(
        'h-full',
        normaliseToolkitStatus(host.overall_status) === 'FAIL' && 'border-destructive/40',
        normaliseToolkitStatus(host.overall_status) === 'WARN' && 'border-amber-500/30',
      )}
      bodyClassName="space-y-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <IngestBadge status={host.ingest_status} />
      </div>
      <p className="text-sm text-muted-foreground">
        {host.customer_name || 'Synology NAS'}
        {(host.display_name && host.display_name !== host.hostname) ? (
          <span>{' · '}{host.hostname}</span>
        ) : null}
      </p>

      {attentionReasons.length > 0 ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Why this status
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-foreground">
            {attentionReasons.map((reason) => (
              <li key={`${reason.source}-${reason.detail}`} className="flex gap-2">
                <span className="shrink-0 pt-0.5">
                  <PostureBadge status={reason.status} />
                </span>
                <span>
                  <span className="font-medium">{reason.source}: </span>
                  {reason.detail}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {host.interpretation && attentionReasons.length === 0 && (
        <p className="text-sm text-muted-foreground">{host.interpretation}</p>
      )}

      <div className="flex flex-wrap gap-4 text-sm">
        {showHyperBackup && (
          <div>
            <p className="text-xs text-muted-foreground">Hyper Backup tasks</p>
            <p className="font-semibold tabular-nums">{host.task_count ?? 0}</p>
          </div>
        )}
        {showCloudSync && (
          <div>
            <p className="text-xs text-muted-foreground">Cloud Sync connections</p>
            <p className="font-semibold tabular-nums">{host.cloudsync_count ?? 0}</p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Last collected
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SourceCollectedAt
            label="NAS health"
            collectedAt={host.health_collected_at || (showHealth ? host.collected_at : null)}
            ingestStatus={host.health_ingest_status}
            postureStatus={host.health_status}
          />
          <SourceCollectedAt
            label="Hyper Backup"
            collectedAt={host.hyperbackup_collected_at}
            ingestStatus={host.hyperbackup_ingest_status}
            postureStatus={host.hyperbackup_status}
          />
          <SourceCollectedAt
            label="Cloud Sync"
            collectedAt={host.cloudsync_collected_at}
            ingestStatus={host.cloudsync_ingest_status}
            postureStatus={host.cloudsync_status}
          />
        </div>
      </div>

      {showCloudSync && (
        <CloudSyncConnectionsPanel
          connections={host.cloudsync_connections}
          postureStatus={host.cloudsync_status}
          count={host.cloudsync_connections?.length ?? host.cloudsync_count ?? 0}
        />
      )}

      {showHealth && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              System health
            </p>
            <PostureBadge status={host.health_status} />
          </div>
          <NasHealthSummary health={host.health} checks={host.checks} />
        </div>
      )}

      {showHyperBackup && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Hyper Backup tasks ({host.tasks?.length ?? host.task_count ?? 0})
            </p>
            <PostureBadge status={host.hyperbackup_status} />
          </div>
          <TaskSummaryTable tasks={host.tasks} />
        </div>
      )}

      {!showHealth && !showHyperBackup && !showCloudSync && (
        <p className="text-sm text-muted-foreground">
          No NAS health, Hyper Backup, or Cloud Sync snapshot received yet for this host.
        </p>
      )}
    </CollapsePanel>
  )
}

export function Nas() {
  const { isAdmin } = useAuth()
  const [summary, setSummary] = useState(null)
  const [hosts, setHosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    setLoading(true)
    setError('')
    Promise.all([
      fetchNasSummary(),
      fetchNasHosts(),
    ])
      .then(([summaryData, hostsData]) => {
        setSummary(summaryData)
        setHosts(hostsData.hosts ?? [])
      })
      .catch(() => {
        setSummary(null)
        setHosts([])
        setError('Could not load NAS health status.')
      })
      .finally(() => setLoading(false))
  }, [])

  const filteredHosts = hosts.filter((host) => {
    if (statusFilter === 'all') return true
    if (statusFilter === 'no_data') return host.ingest_status === 'never'
    if (statusFilter === 'stale') return host.ingest_status === 'stale'
    if (statusFilter === 'updates') return host.health?.dsm?.update_available === true
    const normalised = normaliseToolkitStatus(host.overall_status)
    if (statusFilter === 'pass') return normalised === 'PASS'
    if (statusFilter === 'warn') return normalised === 'WARN'
    if (statusFilter === 'fail') return normalised === 'FAIL'
    return true
  })

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/settings?section=nas-backup">Configure NAS Backup</Link>
          </Button>
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-9">
          <SummaryTile
            label="Configured"
            value={summary.configured_hosts}
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
          <SummaryTile
            label="Pass"
            value={summary.pass_hosts}
            variant="success"
            active={statusFilter === 'pass'}
            onClick={() => setStatusFilter((current) => (current === 'pass' ? 'all' : 'pass'))}
          />
          <SummaryTile
            label="Warning"
            value={summary.warn_hosts}
            variant="warning"
            active={statusFilter === 'warn'}
            onClick={() => setStatusFilter((current) => (current === 'warn' ? 'all' : 'warn'))}
          />
          <SummaryTile
            label="Failed"
            value={summary.fail_hosts}
            variant="destructive"
            active={statusFilter === 'fail'}
            onClick={() => setStatusFilter((current) => (current === 'fail' ? 'all' : 'fail'))}
          />
          <SummaryTile
            label="No data"
            value={summary.no_data_hosts}
            active={statusFilter === 'no_data'}
            onClick={() => setStatusFilter((current) => (current === 'no_data' ? 'all' : 'no_data'))}
          />
          <SummaryTile
            label="Collection stale"
            value={summary.stale_hosts}
            variant="warning"
            active={statusFilter === 'stale'}
            onClick={() => setStatusFilter((current) => (current === 'stale' ? 'all' : 'stale'))}
          />
          <SummaryTile
            label="DSM updates"
            value={summary.update_available_hosts}
            variant="warning"
            active={statusFilter === 'updates'}
            onClick={() => setStatusFilter((current) => (current === 'updates' ? 'all' : 'updates'))}
          />
          <SummaryTile label="Hyper Backup" value={summary.total_tasks} />
          <SummaryTile label="Cloud Sync" value={summary.total_cloudsync_connections} />
        </div>
      )}

      {statusFilter !== 'all' && (
        <p className="text-sm text-muted-foreground">
          Filter: {FILTER_LABELS[statusFilter] || statusFilter}.{' '}
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={() => setStatusFilter('all')}
          >
            Clear filter
          </button>
        </p>
      )}

      {hosts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground space-y-3">
            <p>No Synology NAS devices configured yet.</p>
            {isAdmin ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/settings?section=nas-backup">Add NAS hostnames in NAS Backup settings</Link>
              </Button>
            ) : (
              <p>Ask an administrator to add Synology NAS hostnames in Settings.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredHosts.map((host) => (
            <NasHostCard key={host.hostname} host={host} />
          ))}
        </div>
      )}

      {hosts.length > 0 && filteredHosts.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No NAS devices match the current filter.
          </CardContent>
        </Card>
      )}
    </div>
  )
}
